import { z } from 'zod';

import { partialUpdate } from '@kit/shared/validation';

/**
 * The locales product/shop content can be authored in. MUST mirror the
 * `languages` array in `apps/web/lib/i18n/i18n.settings.ts` (the 15 languages
 * Ballee supports). The shops package can't import from `apps/web`, so the list
 * is duplicated here and kept in sync by `localizedTextSchemaCoversAllLocales`
 * in the schema unit test (fails if the two drift).
 */
export const CONTENT_LOCALES = [
  'en',
  'fr',
  'es',
  'de',
  'it',
  'pt',
  'nl',
  'pl',
  'uk',
  'ru',
  'ja',
  'ko',
  'zh-CN',
  'ar',
  'tr',
] as const;

export type ContentLocale = (typeof CONTENT_LOCALES)[number];

// A single locale value: a non-empty trimmed string when present.
const localeValue = z.string().min(1);
const optionalLocaleValue = localeValue.optional();

/**
 * A localized text map. `en` is required (the guaranteed fallback locale — see
 * i18n.settings `fallbackLng`); every other supported locale is optional, so an
 * owner fills only the languages they have. Empty/whitespace values are
 * rejected per locale (`min(1)`).
 */
export const LocalizedTextSchema = z.object({
  en: localeValue,
  fr: optionalLocaleValue,
  es: optionalLocaleValue,
  de: optionalLocaleValue,
  it: optionalLocaleValue,
  pt: optionalLocaleValue,
  nl: optionalLocaleValue,
  pl: optionalLocaleValue,
  uk: optionalLocaleValue,
  ru: optionalLocaleValue,
  ja: optionalLocaleValue,
  ko: optionalLocaleValue,
  'zh-CN': optionalLocaleValue,
  ar: optionalLocaleValue,
  tr: optionalLocaleValue,
});

export const CreateShopSchema = z.object({
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.optional(),
  slug: z
    .string()
    .min(3)
    .max(50)
    .regex(
      /^[a-z0-9-]+$/,
      'validation:rules.slugMustBeLowercaseLettersNumbersAnd2',
    ),
  default_currency: z.enum(['EUR', 'USD']).default('EUR'),
  account_id: z.guid().optional(),
});

export const UpdateShopSchema = partialUpdate(CreateShopSchema).extend({
  // Marketplace visibility toggle (owner settings). Not on CreateShopSchema —
  // createShop always sets is_active=true; this is the update-only control.
  is_active: z.boolean().optional(),
});

export const CreateArticleSchema = z.object({
  shop_id: z.guid(),
  title: LocalizedTextSchema,
  description: LocalizedTextSchema.optional(),
  features: z.array(LocalizedTextSchema).optional(),
  slug: z.string().min(3).max(100).optional(),
  price_eur: z.number().positive().optional(),
  price_usd: z.number().positive().optional(),
  is_subscription: z.boolean().default(false),
  subscription_interval: z.enum(['day', 'week', 'month', 'year']).optional(),
  subscription_interval_count: z.int().positive().default(1),
  is_published: z.boolean().default(false),
  category_slug: z.string().min(1).max(100).nullable().optional(),
  featured_image_id: z.guid().nullable().optional(),
  media_ids: z.array(z.guid()).optional(),
  featured_image_url: z.url().optional(),
  external_url: z.url().optional(),
  track_inventory: z.boolean().default(false),
  inventory_count: z.int().min(0).default(0),
});

export const UpdateArticleSchema = partialUpdate(CreateArticleSchema).omit({
  shop_id: true,
});

export const CreateOrderSchema = z.object({
  shop_id: z.guid(),
  items: z
    .array(
      z.object({
        article_id: z.guid(),
        quantity: z.int().positive().default(1),
      }),
    )
    .min(1),
  customer_email: z.email(),
  customer_name: z.string().optional(),
  customer_notes: z.string().max(500).optional(),
  currency: z.enum(['EUR', 'USD']).default('EUR'),
});

export const UpdateOrderStatusSchema = z.object({
  status: z.enum([
    'pending_payment',
    'pending_processing',
    'processing',
    'shipped',
    'completed',
    'cancelled',
    'refunded',
  ]),
  internal_notes: z.string().max(1000).optional(),
  tracking_number: z.string().optional(),
});

export type CreateShopInput = z.infer<typeof CreateShopSchema>;
export type UpdateShopInput = z.infer<typeof UpdateShopSchema>;
export type CreateArticleInput = z.infer<typeof CreateArticleSchema>;
export type UpdateArticleInput = z.infer<typeof UpdateArticleSchema>;
export type CreateOrderInput = z.infer<typeof CreateOrderSchema>;
export type UpdateOrderStatusInput = z.infer<typeof UpdateOrderStatusSchema>;
